import React, { useEffect, useState } from 'react';
import portalApi from '../../../../lib/portalApi';
import { X, CheckCircle2, AlertTriangle, ShieldCheck, FileText } from 'lucide-react';

export default function StartupAccessApprovalModal({ startupId, onClose, onUpdated }) {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadStatus = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await portalApi.getAdminStartupAccessStatus(startupId);
      setStatusData(res);
    } catch (err) {
      setError(err.message || 'Failed to load access status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startupId) loadStatus();
  }, [startupId]);

  const handleApprove = async (force = false) => {
    try {
      setActionBusy(true);
      setError('');
      setSuccess('');
      await portalApi.adminApproveStartupAccess(startupId, { forceOverride: force });
      setSuccess('Startup dashboard access activated successfully!');
      await loadStatus();
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.message || 'Failed to approve access');
    } finally {
      setActionBusy(false);
    }
  };

  const handleSuspend = async () => {
    const reason = window.prompt('Enter reason for suspension:');
    if (!reason) return;
    try {
      setActionBusy(true);
      setError('');
      await portalApi.adminSuspendStartupAccess(startupId, reason);
      setSuccess('Startup dashboard access suspended.');
      await loadStatus();
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.message || 'Failed to suspend access');
    } finally {
      setActionBusy(false);
    }
  };

  const handleReactivate = async () => {
    try {
      setActionBusy(true);
      setError('');
      await portalApi.adminReactivateStartupAccess(startupId);
      setSuccess('Startup dashboard access reactivated!');
      await loadStatus();
      if (onUpdated) onUpdated();
    } catch (err) {
      setError(err.message || 'Failed to reactivate access');
    } finally {
      setActionBusy(false);
    }
  };

  if (!startupId) return null;

  const access = statusData?.dashboardAccess || {};
  const currentStatus = access.status || 'not_eligible';
  const checks = statusData?.checks || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#131318] p-6 shadow-2xl relative overflow-hidden">
        <div className="flex items-center justify-between pb-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#FF8EC5]" />
            <h2 className="text-lg font-bold text-white">Startup Dashboard Access</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/50 hover:text-white border border-transparent hover:border-white/10">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-xl border border-red-500/20 bg-red-500/10 text-red-400 text-xs">
            {error}
          </div>
        )}

        {success && (
          <div className="mt-4 p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/10 text-emerald-300 text-xs">
            {success}
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-white/40 text-sm">Verifying eligibility prerequisites...</div>
        ) : (
          <div className="mt-5 space-y-5">
            {/* Status Chip */}
            <div className="flex items-center justify-between p-3.5 rounded-xl border border-white/5 bg-black/40">
              <span className="text-sm text-white/70">Current Access State</span>
              <span
                className={`text-xs uppercase font-semibold px-2.5 py-1 rounded-md border ${
                  currentStatus === 'active'
                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                    : currentStatus === 'suspended'
                      ? 'bg-red-500/15 text-red-400 border-red-500/30'
                      : 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                }`}
              >
                {currentStatus}
              </span>
            </div>

            {/* Verification checklist */}
            <div className="space-y-2.5 text-xs">
              <div className="text-white/50 uppercase tracking-wider font-semibold">Server-Side Eligibility Gates</div>

              <div className="flex items-center justify-between p-3 rounded-xl border border-white/5 bg-black/20">
                <span className="text-white/80">1. Onboarding Completion / Active Cohort Enrollment</span>
                {checks.onboardingComplete ? (
                  <span className="text-emerald-400 flex items-center gap-1 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Verified
                  </span>
                ) : (
                  <span className="text-amber-400 flex items-center gap-1 font-medium">
                    <AlertTriangle className="w-3.5 h-3.5" /> Pending
                  </span>
                )}
              </div>

              <div className="flex items-center justify-between p-3 rounded-xl border border-white/5 bg-black/20">
                <span className="text-white/80">2. Authoritative Signed Accelerator Contract</span>
                {checks.contractSigned ? (
                  <span className="text-emerald-400 flex items-center gap-1 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Signed on File
                  </span>
                ) : (
                  <span className="text-amber-400 flex items-center gap-1 font-medium">
                    <AlertTriangle className="w-3.5 h-3.5" /> Missing / Unsigned
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="pt-4 border-t border-white/10 flex flex-wrap items-center justify-end gap-3">
              {currentStatus === 'active' ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={handleSuspend}
                  className="px-4 py-2 rounded-xl border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-semibold transition-colors disabled:opacity-50"
                >
                  Suspend Access
                </button>
              ) : currentStatus === 'suspended' ? (
                <button
                  type="button"
                  disabled={actionBusy}
                  onClick={handleReactivate}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                >
                  Reactivate Access
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={actionBusy || !statusData?.isEligible}
                    onClick={() => handleApprove(false)}
                    className="px-4 py-2 rounded-xl bg-[#D42B92] hover:bg-[#FF8EC5] text-white text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {actionBusy ? 'Approving...' : 'Approve Dashboard Access'}
                  </button>
                  {!statusData?.isEligible && (
                    <button
                      type="button"
                      disabled={actionBusy}
                      onClick={() => handleApprove(true)}
                      className="px-3 py-2 rounded-xl border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 text-xs transition-colors"
                      title="Superadmin override (bypass contract requirement)"
                    >
                      Superadmin Override
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
